import React, { useEffect, useMemo, useState, useContext } from "react";
import "./../../assets/css/notification/NotificationsPage.css";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import LoaderNotifications from "../../components/loader/notification/LoaderNotifications";
import useNotification from "../../hooks/useNotification";
import { useNotificationRealtime } from "../../contexts/NotificationContext";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
dayjs.extend(relativeTime);
dayjs.locale("fr");

export default function NotificationsPage() {
  const STEP = 4;

  const [visibleCount, setVisibleCount] = useState(STEP);
  const [loaderNotification, setLoaderNotification] = useState(false);

  const { user, isLogged } = useContext(AuthContext);
  const navigate = useNavigate();

  const notifCtx = useNotificationRealtime();
  const notifications = notifCtx?.notifications || [];
  const setNotifications = notifCtx?.setNotifications;

  const { getNotifications, readNotification, readAllNotifications } =
    useNotification();

  useEffect(() => {
    if (!isLogged || !user?._id) return;
    setLoaderNotification(true);

    getNotifications()
      .then((res) => {
        const data = res?.data?.data || [];
        if (setNotifications) setNotifications(data);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoaderNotification(false));
  }, [isLogged, user?._id]);

  const myNotifications = useMemo(() => {
    if (!user?._id) return [];
    return (notifications || [])
      .filter((n) => String(n.id_utilisateur) === String(user._id))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [notifications, user?._id]);

  const allNotifications = useMemo(() => {
    return myNotifications.map((n) => ({
      id: n._id,
      title: n.type === 1 ? "Nouveau message" : "Notification",
      desc: n.message,
      time: n.createdAt ? dayjs(n.createdAt).fromNow() : "Date inconnue",
      read: n.vu !== 2,
      raw: n,
    }));
  }, [myNotifications]);

  const visibleNotifications = allNotifications.slice(0, visibleCount);
  const remaining = allNotifications.length - visibleNotifications.length;

  const breadcrumbItems = [
    { label: "Accueil", link: "/" },
    { label: "Mon compte", link: "/utilisateur/compte" },
    { label: "Notifications" },
  ];

  const markAsReadLocal = (id) => {
    if (!setNotifications) return;
    setNotifications((prev) =>
      (prev || []).map((x) =>
        String(x._id) === String(id) ? { ...x, vu: 1 } : x
      )
    );
  };

  const handleOpenNotification = async (rawNotif) => {
    try {
      if (rawNotif?.vu === 2) {
        markAsReadLocal(rawNotif._id);
      }

      if (rawNotif?.vu === 2) {
        await readNotification(rawNotif._id).catch((e) =>
          console.error("Erreur readNotification:", e)
        );
      }

      if (rawNotif?.id_article) {
        navigate(`/article/details/${rawNotif.id_article}`);
      }
    } catch (e) {
      console.error("Erreur open notification:", e);
    }
  };

  const handleReadAll = async () => {
    if (!user?._id || !setNotifications) return;

    // Optimistic UI
    setNotifications((prev) =>
      (prev || []).map((n) =>
        String(n.id_utilisateur) === String(user._id) ? { ...n, vu: 1 } : n
      )
    );

    try {
      await readAllNotifications(user._id);
    } catch (e) {
      console.error("Erreur readAll:", e);
    }
  };

  return (
    <div className="notifPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="notifGrid">
          <section className="notifCard notifCardCenter">
            <div className="notifHeader">
              <div className="notifCardTitle">Mes notifications</div>

              <button
                type="button"
                className="notifBtn notifBtnSmall"
                onClick={handleReadAll}
                disabled={allNotifications.every((n) => n.read)}
                title="Marquer tout comme lu"
              >
                Tout marquer comme lu
              </button>
            </div>

            {loaderNotification ? (
              <LoaderNotifications />
            ) : allNotifications.length === 0 ? (
              <div style={{ padding: "14px 0" }}>
                Aucune notification pour le moment.
              </div>
            ) : (
              <ul className="notifList">
                {visibleNotifications.map((n) => (
                  <li
                    key={n.id}
                    className={`notifItem ${n.read ? "read" : ""}`}
                    onClick={() => handleOpenNotification(n.raw)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleOpenNotification(n.raw);
                    }}
                  >
                    <div className="notifDot" aria-hidden="true" />
                    <div className="notifMain">
                      <div className="notifRowTop">
                        <div className="notifLabel" title={n.desc}>
                          {n.title}
                        </div>
                        <div className="notifTime">{n.time}</div>
                      </div>

                      <div className="notifDesc">{n.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {!loaderNotification && allNotifications.length > 0 && (
              <div className="notifActions">
                <button
                  type="button"
                  className="notifBtn"
                  onClick={() =>
                    setVisibleCount((c) =>
                      Math.min(c + STEP, allNotifications.length)
                    )
                  }
                  disabled={remaining <= 0}
                  aria-label="Voir plus de notifications"
                  title={remaining > 0 ? "Voir plus" : "Tout est affiché"}
                  style={
                    remaining <= 0
                      ? { opacity: 0.55, cursor: "default" }
                      : undefined
                  }
                >
                  {remaining > 0
                    ? `Voir plus (${remaining})`
                    : "Tout est affiché"}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
